import { AlcoveEdgeName, CornerName, EdgeName, LineFrame, OuterWhich, Point } from '@kaiizen/kai-floor-plan';

/**
 * A recursive geometry path — a bounding-volume hierarchy over the room
 * perimeter. Leaves are line segments tagged with the source-geometry they
 * belong to; groups bundle children under one bounding box so a hit-test (or
 * any spatial query) can reject a whole subtree by its box before recursing.
 *
 * Groups carry an optional `ref` too: the alcove body and the whole wall are
 * selectable targets, not just their constituent line segments.
 *
 * DOM-free by design: this is how non-DOM platforms (e.g. a React Native port)
 * hit-test, where there is no `data-handle` + `closest()` to lean on.
 */

/** What a path node refers to — the single geometry-part identity. */
export type PerimRef =
  | { k: 'room' }
  | { k: 'wall'; edge: EdgeName; gs: number; ge: number } // a flat run of a main wall; gs/ge = along-edge span
  | { k: 'seg'; edge: EdgeName; index: number } // a non-alcove edge segment (door/window/opening)
  | { k: 'alcove'; edge: EdgeName; index: number } // an alcove body
  | { k: 'alcoveWall'; edge: EdgeName; index: number; wall: AlcoveEdgeName }
  | { k: 'roomCorner'; corner: CornerName }
  | { k: 'alcoveCorner'; edge: EdgeName; index: number; which: OuterWhich };

export interface Bounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
}

export type GeoPath = LinePath | GroupPath;

export interface LinePath {
  kind: 'line';
  line: LineFrame;
  ref: PerimRef;
  /** Wall height (resolved against ceiling). 0 ⇒ open wall (drawn dashed). */
  height: number;
}

export interface GroupPath {
  kind: 'group';
  ref?: PerimRef;
  bounds: Bounds;
  children: GeoPath[];
}

const EMPTY_BOUNDS: Bounds = { minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity };

function lineBounds(l: LineFrame): Bounds {
  return {
    minX: Math.min(l.start.x, l.end.x),
    minY: Math.min(l.start.y, l.end.y),
    maxX: Math.max(l.start.x, l.end.x),
    maxY: Math.max(l.start.y, l.end.y),
  };
}

function mergeBounds(a: Bounds, b: Bounds): Bounds {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
  };
}

export function boundsOf(p: GeoPath): Bounds {
  return p.kind === 'line' ? lineBounds(p.line) : p.bounds;
}

/** Bundle children into a group, computing the enclosing bounds. */
export function group(children: GeoPath[], ref?: PerimRef): GroupPath {
  const bounds = children.reduce((b, c) => mergeBounds(b, boundsOf(c)), EMPTY_BOUNDS);
  return { kind: 'group', ref, bounds, children };
}

/** All line leaves, in walk order. */
export function leaves(p: GeoPath): LinePath[] {
  return p.kind === 'line' ? [p] : p.children.flatMap(leaves);
}

/**
 * The leaf endpoints as one ordered polyline (start of #0, then every end).
 * Assumes the leaves are contiguous (a single wall or alcove, or the whole
 * room) — disjoint leaves (e.g. a selected wall's gaps) should be stroked
 * per-leaf instead.
 */
export function outline(p: GeoPath): Point[] {
  const ls = leaves(p);
  if (ls.length === 0) return [];
  return [ls[0].line.start, ...ls.map(l => l.line.end)];
}

function boundsHit(b: Bounds, pt: Point, tol: number): boolean {
  return pt.x >= b.minX - tol && pt.x <= b.maxX + tol && pt.y >= b.minY - tol && pt.y <= b.maxY + tol;
}

/** Distance from a point to a line segment. */
function distToSegment(pt: Point, l: LineFrame): number {
  const vx = l.end.x - l.start.x, vy = l.end.y - l.start.y;
  const len2 = vx * vx + vy * vy;
  if (len2 < 1e-12) return Math.hypot(pt.x - l.start.x, pt.y - l.start.y);
  let t = ((pt.x - l.start.x) * vx + (pt.y - l.start.y) * vy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(pt.x - (l.start.x + t * vx), pt.y - (l.start.y + t * vy));
}

interface Hit {
  leaf: LinePath;
  dist: number;
}

function nearest(p: GeoPath, pt: Point, tol: number): Hit | null {
  if (p.kind === 'line') {
    const dist = distToSegment(pt, p.line);
    return dist <= tol ? { leaf: p, dist } : null;
  }
  if (!boundsHit(p.bounds, pt, tol)) return null; // prune the whole subtree
  let best: Hit | null = null;
  for (const c of p.children) {
    const h = nearest(c, pt, tol);
    if (h && (!best || h.dist < best.dist)) best = h;
  }
  return best;
}

/** The nearest line leaf within `tol` of `pt`, or null. Prunes by group bounds. */
export function hitTest(p: GeoPath, pt: Point, tol: number): LinePath | null {
  return nearest(p, pt, tol)?.leaf ?? null;
}
